import { FileText, RefreshCw, Search } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '../../../components/ui/button.js';
import { Input } from '../../../components/ui/input.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select.js';
import { cn } from '../../../lib/utils.js';
import type { ScenarioStatusFilter } from '../types.js';

/**
 * Search + filter-drawer + status-pills + view-select strip. Mirrors
 * the toolbar block the legacy renders above `ScenarioDefaultTable`.
 * `filterDrawer` is rendered to the right of the search input — the
 * page owns the drawer state so the toolbar stays presentation-only.
 */
interface ScenarioToolbarProps {
  search: string;
  onSearchChange: (v: string) => void;
  status: ScenarioStatusFilter;
  onStatusChange: (s: ScenarioStatusFilter) => void;
  counts: { all: number; active: number; inactive: number };
  showViewSelect?: boolean;
  view?: 'default' | 'workfront-objects';
  onViewChange?: (v: 'default' | 'workfront-objects') => void;
  /** Slot for the `ScenarioFilterDrawer` trigger button. */
  filterDrawer?: ReactNode;
  /** Optional refresh handler — invalidates the list / sub-table
   *  queries. When omitted the button isn't rendered. */
  onRefresh?: () => void;
  /** `true` while the list query is refetching — drives the spinner. */
  isRefreshing?: boolean;
}

export function ScenarioToolbar({
  search,
  onSearchChange,
  status,
  onStatusChange,
  counts,
  showViewSelect = false,
  view = 'default',
  onViewChange,
  filterDrawer,
  onRefresh,
  isRefreshing = false,
}: ScenarioToolbarProps): JSX.Element {
  return (
    // Grid-3 columns so the pills sit in the middle column and are
    // perfectly centered against the table below, regardless of
    // left- and right-cluster widths. Each column wraps its own
    // controls in a flex row so we keep the gap behaviour. The left
    // and right columns use `min-w-0` so the pills can expand evenly
    // when the toolbar gets cramped.
    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 border-b border-foreground/10 bg-foreground/[0.03] px-6 py-3">
      <div className="flex min-w-0 items-center gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/40" />
          <Input
            type="search"
            placeholder="Search scenarios..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="border-foreground/15 bg-foreground/5 pl-9 text-foreground placeholder:text-foreground/40 focus-visible:ring-white/30"
          />
        </div>
        {filterDrawer ? <div className="flex items-center">{filterDrawer}</div> : null}
      </div>

      <StatusPills status={status} onChange={onStatusChange} counts={counts} />

      <div className="flex min-w-0 items-center justify-end gap-3">
        {onRefresh ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={isRefreshing}
            aria-label="Refresh scenarios"
            className="border-foreground/15 bg-foreground/5 text-foreground/80 hover:border-foreground/40 hover:text-foreground"
          >
            <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
          </Button>
        ) : null}

        {showViewSelect ? (
          <Select
            value={view}
            onValueChange={(v) => onViewChange?.(v as 'default' | 'workfront-objects')}
          >
            <SelectTrigger className="w-[220px] border-foreground/15 bg-foreground/5 text-foreground">
              <FileText className="mr-2 h-4 w-4 text-foreground/60" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Default</SelectItem>
              <SelectItem value="workfront-objects">Workfront Objects (All)</SelectItem>
            </SelectContent>
          </Select>
        ) : null}
      </div>
    </div>
  );
}

function StatusPills({
  status,
  onChange,
  counts,
  className,
}: {
  status: ScenarioStatusFilter;
  onChange: (s: ScenarioStatusFilter) => void;
  counts: { all: number; active: number; inactive: number };
  className?: string;
}): JSX.Element {
  const items: ReadonlyArray<{ key: ScenarioStatusFilter; label: string; count: number }> = [
    { key: 'all', label: 'All', count: counts.all },
    { key: 'active', label: 'Active', count: counts.active },
    { key: 'inactive', label: 'Inactive', count: counts.inactive },
  ];
  return (
    <div
      className={cn(
        'inline-flex rounded-md border border-foreground/15 bg-foreground/[0.04] p-1',
        className,
      )}
    >
      {items.map((it) => {
        const active = status === it.key;
        return (
          <button
            key={it.key}
            type="button"
            onClick={() => onChange(it.key)}
            className={cn(
              'flex items-center gap-2 rounded px-3 py-1 text-sm transition-colors',
              active
                ? 'bg-foreground/15 text-foreground'
                : 'text-foreground/70 hover:text-foreground',
            )}
          >
            <span>{it.label}</span>
            <span
              className={cn(
                'rounded px-1.5 py-0.5 text-xs',
                active ? 'bg-foreground/20 text-foreground' : 'bg-foreground/10 text-foreground/60',
              )}
            >
              {it.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}
